import React, { useEffect, useState, useCallback } from "react";
import AppShell from "@/components/layout/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Mail, Phone, Clock, MessageSquare, Trash2, CheckCircle2,
  MailOpen, Archive, ArrowLeft, Loader2, Inbox
} from "lucide-react";

const STATUS_MAP = {
  new:      { label: "New",      color: "bg-blue-500/10 text-blue-700 border-blue-200" },
  read:     { label: "Read",     color: "bg-amber-500/10 text-amber-700 border-amber-200" },
  replied:  { label: "Replied",  color: "bg-emerald-500/10 text-emerald-700 border-emerald-200" },
  archived: { label: "Archived", color: "bg-zinc-500/10 text-zinc-600 border-zinc-200" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export default function ContactSubmissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/admin/contact-submissions", { params });
      setItems(data);
    } catch {
      toast.error("Failed to load contact submissions");
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (item) => {
    setSelected(item);
    setNotes(item.notes || "");
    // Auto-mark as read if new
    if (item.status === "new") {
      try {
        await api.patch(`/admin/contact-submissions/${item.id}`, { status: "read" });
        setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, status: "read" } : i));
      } catch { /* silent */ }
    }
  };

  const updateStatus = async (id, status) => {
    setSaving(true);
    try {
      await api.patch(`/admin/contact-submissions/${id}`, { status });
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, status } : i));
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
    setSaving(false);
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.patch(`/admin/contact-submissions/${selected.id}`, { notes });
      setItems((prev) => prev.map((i) => i.id === selected.id ? { ...i, notes } : i));
      toast.success("Notes saved");
    } catch {
      toast.error("Failed to save notes");
    }
    setSaving(false);
  };

  const deleteSubmission = async (id) => {
    setDeleting(id);
    try {
      await api.delete(`/admin/contact-submissions/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
    setDeleting(null);
  };

  const newCount = items.filter((i) => i.status === "new").length;

  return (
    <AppShell variant="admin" title="Contact Enquiries">
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[160px] h-9 rounded-lg" data-testid="contact-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All submissions</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="read">Read</SelectItem>
                <SelectItem value="replied">Replied</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            {newCount > 0 && (
              <Badge variant="secondary" className="bg-blue-500/10 text-blue-700 border-blue-200" data-testid="contact-new-count">
                {newCount} new
              </Badge>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            {items.length} enquir{items.length === 1 ? "y" : "ies"}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
          </div>
        )}

        {/* Empty */}
        {!loading && items.length === 0 && (
          <Card className="p-10 rounded-2xl text-center" data-testid="contact-empty">
            <Inbox className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
            <h3 className="font-semibold">No enquiries yet</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Contact form submissions from the landing page will appear here.
            </p>
          </Card>
        )}

        {/* List */}
        {!loading && items.length > 0 && (
          <div className="space-y-2">
            {items.map((item) => {
              const st = STATUS_MAP[item.status] || STATUS_MAP.new;
              return (
                <Card
                  key={item.id}
                  className={`p-4 rounded-xl cursor-pointer hover:border-foreground/20 transition-all ${item.status === "new" ? "border-blue-300/50 bg-blue-50/30" : ""}`}
                  onClick={() => openDetail(item)}
                  data-testid={`contact-row-${item.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center shrink-0 mt-0.5">
                      <MessageSquare className="h-4 w-4 text-[hsl(var(--accent))]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm truncate">{item.name}</span>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${st.color}`}>{st.label}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{item.email}</span>
                        {item.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{item.phone}</span>}
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{timeAgo(item.created_at)}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{item.message}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent className="sm:max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="contact-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg">{selected?.name}</DialogTitle>
            <DialogDescription className="text-xs">
              Submitted {selected?.created_at ? new Date(selected.created_at).toLocaleString() : ""}
            </DialogDescription>
          </DialogHeader>

          {selected && (
            <div className="space-y-4 pt-2">
              {/* Contact info */}
              <div className="flex flex-wrap gap-3 text-sm">
                <a href={`mailto:${selected.email}`} className="flex items-center gap-1.5 text-[hsl(var(--accent))] hover:underline" data-testid="contact-detail-email">
                  <Mail className="h-3.5 w-3.5" /> {selected.email}
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="flex items-center gap-1.5 text-[hsl(var(--accent))] hover:underline" data-testid="contact-detail-phone">
                    <Phone className="h-3.5 w-3.5" /> {selected.phone}
                  </a>
                )}
              </div>

              {/* Message */}
              <div className="rounded-xl bg-[hsl(var(--secondary))]/60 p-4">
                <p className="text-sm whitespace-pre-wrap" data-testid="contact-detail-message">{selected.message}</p>
              </div>

              {/* Status actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full h-8 text-xs gap-1.5"
                  disabled={saving || selected.status === "replied"}
                  onClick={() => updateStatus(selected.id, "replied")}
                  data-testid="contact-mark-replied"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" /> Mark replied
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full h-8 text-xs gap-1.5"
                  disabled={saving || selected.status === "archived"}
                  onClick={() => updateStatus(selected.id, "archived")}
                  data-testid="contact-mark-archived"
                >
                  <Archive className="h-3.5 w-3.5" /> Archive
                </Button>
                <a
                  href={`mailto:${selected.email}?subject=Re: Your enquiry to Derwent Cricket Club&body=%0A%0A--- Original message ---%0A${encodeURIComponent(selected.message)}`}
                  className="inline-flex"
                >
                  <Button size="sm" className="rounded-full h-8 text-xs gap-1.5 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="contact-reply-email">
                    <Mail className="h-3.5 w-3.5" /> Reply by email
                  </Button>
                </a>
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Admin notes</label>
                <Textarea
                  rows={2}
                  placeholder="Add internal notes…"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="resize-none text-sm"
                  data-testid="contact-notes-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full h-8 text-xs"
                  disabled={saving || notes === (selected.notes || "")}
                  onClick={saveNotes}
                  data-testid="contact-save-notes"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                  Save notes
                </Button>
              </div>

              {/* Delete */}
              <div className="pt-2 border-t border-border">
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-full h-8 text-xs gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
                  disabled={deleting === selected.id}
                  onClick={() => deleteSubmission(selected.id)}
                  data-testid="contact-delete-btn"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete submission
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
